import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { FileUp, Trash2, RefreshCw, Info } from "lucide-react";

const MAX_KB_FILE_BYTES = 16 * 1024 * 1024;
const MAX_KB_DOCS = 10;
const KINDS = [
  { value: "politica", label: "Política" },
  { value: "manual", label: "Manual" },
  { value: "procedimento", label: "Procedimento" },
  { value: "regulamento", label: "Regulamento" },
  { value: "institucional", label: "Institucional" },
  { value: "outro", label: "Outro" },
] as const;

const MIME_BY_EXT: Record<string, string> = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  md: "text/markdown",
  txt: "text/plain",
};

type KbRow = {
  id: string;
  display_name: string;
  document_kind: string;
  purpose: string;
  active_version_id: string | null;
  versions: {
    id: string;
    generation: number;
    status: string;
    original_filename: string | null;
    error_message: string | null;
    mime_type: string;
  }[];
};

async function sha256File(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

function statusLabel(doc: KbRow): string {
  const active = doc.versions.find((v) => v.id === doc.active_version_id);
  const processing = doc.versions.some((v) => v.status === "processing" && v.id !== doc.active_version_id);
  if (processing && active?.status === "ready") return "atualizando…";
  if (active?.status === "ready") return "pronto";
  const latest = [...doc.versions].sort((a, b) => b.generation - a.generation)[0];
  if (latest?.status === "processing") return "processando…";
  if (latest?.status === "failed") return latest.error_message || "falhou";
  return "aguardando";
}

export function KnowledgeBaseSection({
  workspaceId,
  agentId,
  kbOverridesFaq,
  onKbOverridesFaqChange,
  onDocCountChange,
}: {
  workspaceId: string;
  agentId: string | undefined;
  kbOverridesFaq: boolean;
  onKbOverridesFaqChange: (value: boolean) => void;
  onDocCountChange?: (count: number) => void;
}) {
  const { toast } = useToast();
  const [docs, setDocs] = useState<KbRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [kind, setKind] = useState<string>("manual");
  const [purpose, setPurpose] = useState("");

  const loadDocs = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("kb_documents")
      .select("id, display_name, document_kind, purpose, active_version_id")
      .eq("agent_id", agentId)
      .eq("workspace_id", workspaceId)
      .is("deleted_at", null)
      .order("created_at", { ascending: true });
    if (error) {
      setLoading(false);
      toast({ title: "Erro ao carregar documentos", description: error.message, variant: "destructive" });
      return;
    }
    const ids = (data || []).map((row) => row.id);
    if (ids.length > 0) {
      const { data: versionRows } = await supabase
        .from("kb_document_versions")
        .select("id, document_id, generation, status, original_filename, error_message, mime_type")
        .in("document_id", ids);
      setDocs(
        (data || []).map((row) => ({
          ...row,
          versions: (versionRows || []).filter((v: { document_id: string }) => v.document_id === row.id),
        })),
      );
    } else {
      setDocs([]);
    }
    const count = (data || []).length;
    onDocCountChange?.(count);
    setLoading(false);
  }, [agentId, workspaceId, toast, onDocCountChange]);

  useEffect(() => {
    void loadDocs();
  }, [loadDocs]);

  const readFnError = async (e: any): Promise<string> => {
    try {
      const body = await e?.context?.json?.();
      if (body?.error) return String(body.error);
    } catch { /* ignore */ }
    return "Não foi possível enviar o arquivo. Tente novamente.";
  };

  const upload = async (
    file: File,
    replaceDocumentId?: string,
    identity?: { display_name: string; document_kind: string; purpose: string },
  ) => {
    if (!agentId) return;
    // Substituir arquivo usa a identidade do próprio documento — nunca o formulário de novo envio.
    const idName = (identity?.display_name ?? displayName).trim();
    const idKind = identity?.document_kind ?? kind;
    const idPurpose = (identity?.purpose ?? purpose).trim();
    const isNewUpload = !replaceDocumentId;
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    const mime = MIME_BY_EXT[ext];
    if (!mime) {
      toast({ title: "Tipo não permitido", description: "Use PDF, DOCX, MD ou TXT.", variant: "destructive" });
      return;
    }
    if (file.size > MAX_KB_FILE_BYTES) {
      toast({ title: "Arquivo grande demais", description: "O limite é 16 MB.", variant: "destructive" });
      return;
    }
    if (!idName || !idPurpose) {
      toast({ title: "Identidade obrigatória", description: "Preencha nome, tipo e propósito antes do upload.", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const content_hash = await sha256File(file);
      const { data: presign, error: presignErr } = await supabase.functions.invoke("presign-kb-upload", {
        body: {
          workspace_id: workspaceId,
          agent_id: agentId,
          file_name: file.name,
          mime_type: mime,
          size: file.size,
          display_name: idName,
          document_kind: idKind,
          purpose: idPurpose,
          replace_document_id: replaceDocumentId || null,
          content_hash,
        },
      });
      if (presignErr) throw new Error(await readFnError(presignErr));
      if (presign?.error) throw new Error(presign.error);
      if (presign?.skipped) {
        toast({ title: "Já indexado", description: "Esse arquivo já está na base deste agente. Você pode editar o nome e o propósito sem reenviar." });
        if (isNewUpload) { setDisplayName(""); setPurpose(""); }
        await loadDocs();
        return;
      }
      const putResp = await fetch(presign.upload_url, {
        method: "PUT",
        headers: presign.headers || { "Content-Type": mime },
        body: file,
      });
      if (!putResp.ok) throw new Error("Falha no envio do arquivo");
      const { data: ingest, error: ingestErr } = await supabase.functions.invoke("ingest-kb-document", {
        body: {
          workspace_id: workspaceId,
          agent_id: agentId,
          document_id: presign.document_id,
          version_id: presign.version_id,
        },
      });
      if (ingestErr) throw new Error(await readFnError(ingestErr));
      if (ingest?.error) throw new Error(ingest.error);
      if (ingest?.skipped) {
        toast({ title: "Já indexado", description: ingest.message || "Esse arquivo já está na base." });
      } else {
        toast({ title: "Documento enviado", description: "A indexação continua em segundo plano." });
      }
      if (isNewUpload) { setDisplayName(""); setPurpose(""); }
      await loadDocs();
    } catch (err: any) {
      toast({ title: "Erro no upload", description: err.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const saveIdentity = async (doc: KbRow) => {
    const { error } = await supabase.functions.invoke("manage-kb-document", {
      body: {
        action: "update_identity",
        workspace_id: workspaceId,
        agent_id: agentId,
        document_id: doc.id,
        display_name: doc.display_name,
        document_kind: doc.document_kind,
        purpose: doc.purpose,
      },
    });
    if (error) {
      toast({ title: "Erro ao salvar identidade", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Identidade atualizada", description: "Não foi necessário reindexar." });
  };

  const removeDoc = async (id: string) => {
    const { error } = await supabase.functions.invoke("manage-kb-document", {
      body: {
        action: "delete",
        workspace_id: workspaceId,
        agent_id: agentId,
        document_id: id,
      },
    });
    if (error) {
      toast({ title: "Erro ao excluir", description: error.message, variant: "destructive" });
      return;
    }
    await loadDocs();
  };

  if (!agentId) {
    return (
      <div className="space-y-4">
        <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/40">
          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-sm text-blue-900 dark:text-blue-100">
            Salve o agente uma vez para enviar documentos. Depois disso, você poderá adicionar até{" "}
            {MAX_KB_DOCS} arquivos (PDF, DOCX, MD ou TXT, até 16 MB cada).
          </AlertDescription>
        </Alert>
        <div className="space-y-2 rounded-lg border border-dashed border-muted-foreground/30 p-3 opacity-60">
          <Input disabled placeholder="Nome (ex.: Política de Trocas)" />
          <Select disabled value="manual">
            <SelectTrigger><SelectValue placeholder="Tipo" /></SelectTrigger>
          </Select>
          <Textarea disabled placeholder="Propósito: quando usar este arquivo" className="min-h-[56px]" />
          <Button type="button" variant="outline" size="sm" disabled>
            <FileUp className="w-4 h-4 mr-2" /> Enviar arquivo
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{0}/{MAX_KB_DOCS} documentos</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Até 10 arquivos (PDF, DOCX, MD ou TXT, 16 MB). Independente do catálogo de produtos.
      </p>
      <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
        <div>
          <Label htmlFor="kb-overrides-faq" className="text-sm font-medium">Priorizar documentos sobre a FAQ</Label>
          <p className="text-xs text-muted-foreground">Desligado por padrão. A FAQ continua valendo em perguntas genéricas.</p>
        </div>
        <Switch id="kb-overrides-faq" checked={kbOverridesFaq} onCheckedChange={onKbOverridesFaqChange} />
      </div>

      <div className="space-y-2 rounded-lg border border-dashed p-3">
        <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Nome (ex.: Política de Trocas)" maxLength={200} />
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            {KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Textarea value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="Propósito: quando usar este arquivo" maxLength={280} className="min-h-[56px]" />
        <label className="inline-flex">
          <input
            type="file"
            className="hidden"
            accept=".pdf,.docx,.md,.txt,application/pdf,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            disabled={uploading || docs.length >= MAX_KB_DOCS}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) void upload(file);
            }}
          />
          <Button type="button" variant="outline" size="sm" disabled={uploading || docs.length >= MAX_KB_DOCS} asChild>
            <span><FileUp className="w-4 h-4 mr-2" />{uploading ? "Enviando…" : "Enviar arquivo"}</span>
          </Button>
        </label>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{docs.length}/{MAX_KB_DOCS} documentos</p>
        <Button type="button" variant="ghost" size="sm" onClick={() => void loadDocs()} disabled={loading}>
          <RefreshCw className="w-4 h-4 mr-1" /> Atualizar
        </Button>
      </div>

      <div className="space-y-3">
        {docs.map((doc) => (
          <div key={doc.id} className="rounded-lg border border-border p-3 space-y-2 bg-muted/30">
            <div className="flex items-start gap-2">
              <div className="flex-1 space-y-2">
                <Input
                  value={doc.display_name}
                  onChange={(e) => setDocs((prev) => prev.map((d) => d.id === doc.id ? { ...d, display_name: e.target.value } : d))}
                  onBlur={() => void saveIdentity(doc)}
                />
                <Select
                  value={doc.document_kind}
                  onValueChange={(value) => {
                    const next = { ...doc, document_kind: value };
                    setDocs((prev) => prev.map((d) => d.id === doc.id ? next : d));
                    void saveIdentity(next);
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Textarea
                  value={doc.purpose}
                  maxLength={280}
                  className="min-h-[52px]"
                  onChange={(e) => setDocs((prev) => prev.map((d) => d.id === doc.id ? { ...d, purpose: e.target.value } : d))}
                  onBlur={() => void saveIdentity(doc)}
                />
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={() => void removeDoc(doc.id)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center justify-between gap-2">
              <Badge variant="secondary">{statusLabel(doc)}</Badge>
              <label className="inline-flex">
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.docx,.md,.txt,application/pdf,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (file) {
                      void upload(file, doc.id, {
                        display_name: doc.display_name,
                        document_kind: doc.document_kind,
                        purpose: doc.purpose,
                      });
                    }
                  }}
                />
                <Button type="button" variant="outline" size="sm" asChild>
                  <span>Substituir arquivo</span>
                </Button>
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
